/**
 * Cheap collision system. We don't run a full physics engine — instead a
 * registry of (centre, radius) circles in XZ. The character movement step
 * checks each circle and pushes the player out along the surface normal.
 *
 * Buildings register their footprint at construction time; the movement
 * loop calls resolveCollision(pos) every tick.
 */

export interface Circle {
  cx: number;
  cz: number;
  r: number;
}

const blockers: Circle[] = [];

export function addBlocker(c: Circle): void {
  blockers.push(c);
}

export function clearBlockers(): void {
  blockers.length = 0;
}

/**
 * Push (x, z) out of every blocker circle. Mutating return — we hand back
 * the same {x, z} to keep the call site cheap. Player radius bakes the
 * character's own footprint into the test.
 */
const PLAYER_R = 0.55;

export function resolveCollision(pos: { x: number; z: number }): { x: number; z: number; collided: boolean } {
  let collided = false;
  for (const b of blockers) {
    const dx = pos.x - b.cx;
    const dz = pos.z - b.cz;
    const dsq = dx * dx + dz * dz;
    const minD = b.r + PLAYER_R;
    if (dsq < minD * minD && dsq > 0.0001) {
      const d = Math.sqrt(dsq);
      const push = (minD - d) / d;
      pos.x += dx * push;
      pos.z += dz * push;
      collided = true;
    }
  }
  return { x: pos.x, z: pos.z, collided };
}
